<script setup lang="ts">
/**
 * AdminSidebar — левый сайдбар нового админ-лейаута (admin-kit).
 * Сворачивается в рейку (только иконки) на lg+; на мобиле — off-canvas.
 * Цвета только из токенов (классы hui-*). Иконки — heroicons через @nuxt/icon.
 */
import { useAuthStore, useAdminSessionManager } from '~/features/auth'
import { canAccessAdminPath } from '~/shared/config'

withDefaults(defineProps<{ collapsed?: boolean, mobileOpen?: boolean }>(), {
  collapsed: false,
  mobileOpen: false,
})

const emit = defineEmits<{ (e: 'close'): void }>()

defineOptions({ name: 'AdminSidebar' })

const route = useRoute()
const auth = useAuthStore()

interface NavItem { label: string, to: string, icon: string, match?: string }
interface NavSection { title: string, items: NavItem[] }

const NAV: NavSection[] = [
  {
    title: 'Блюда',
    items: [
      { label: 'Товары', to: '/administration/products', icon: 'heroicons:cube' },
      { label: 'Категории', to: '/administration/categories', icon: 'heroicons:squares-2x2' },
      { label: 'Теги', to: '/administration/tags', icon: 'heroicons:tag' },
      { label: 'Добавки', to: '/administration/addon', icon: 'heroicons:plus-circle' },
      { label: 'Исключения', to: '/administration/excludeIngredients', icon: 'heroicons:no-symbol' },
      { label: 'Группы', to: '/administration/productGroup', icon: 'heroicons:rectangle-group' },
      { label: 'Блюда по филиалам', to: '/administration/dish-groups', icon: 'heroicons:square-3-stack-3d' },
      { label: 'Комбо', to: '/administration/combos', icon: 'heroicons:gift' },
    ],
  },
  {
    title: 'Маркетинг',
    items: [
      { label: 'Удержание', to: '/administration/retention', icon: 'heroicons:arrow-trending-up' },
      { label: 'Промокоды', to: '/administration/promocodes', icon: 'heroicons:ticket' },
      { label: 'Рефералка', to: '/administration/referral', icon: 'heroicons:user-group' },
      { label: 'Новости', to: '/administration/news-slider', icon: 'heroicons:newspaper' },
      { label: 'Лояльность', to: '/administration/loyalty/settings', icon: 'heroicons:star', match: '/administration/loyalty' },
      { label: 'Средний чек', to: '/administration/average-check', icon: 'heroicons:gift-top' },
      { label: 'Подарок за частоту', to: '/administration/weekly-gift', icon: 'heroicons:calendar-days' },
    ],
  },
  {
    title: 'Коммуникации',
    items: [
      { label: 'Уведомления', to: '/administration/notifications', icon: 'heroicons:bell' },
      { label: 'Чат поддержки', to: '/administration/support', icon: 'heroicons:chat-bubble-left-right' },
      { label: 'Стикеры чата', to: '/administration/support/stickers', icon: 'heroicons:face-smile' },
    ],
  },
  {
    title: 'Заведение',
    items: [
      { label: 'Заказы', to: '/administration/orders', icon: 'heroicons:clipboard-document-list' },
      { label: 'Яндекс Доставка', to: '/administration/yandex-delivery', icon: 'heroicons:truck' },
      { label: 'Загрузка кухни', to: '/administration/workload-control', icon: 'heroicons:fire' },
      { label: 'Отзывы', to: '/administration/reviews', icon: 'heroicons:star' },
      { label: 'Пользователи', to: '/administration/users', icon: 'heroicons:user-circle' },
      { label: 'Аналитика', to: '/administration/analytics', icon: 'heroicons:chart-bar' },
      { label: 'Воронки', to: '/administration/funnels', icon: 'heroicons:funnel' },
      {
        label: 'Мобильная аналитика',
        to: '/administration/mobile-analytics',
        icon: 'heroicons:device-phone-mobile',
      },
      { label: 'Сотрудники', to: '/administration/staff', icon: 'heroicons:user-group' },
      { label: 'Настройки', to: '/administration/settings', icon: 'heroicons:cog-6-tooth' },
    ],
  },
  {
    title: 'Справка',
    items: [
      { label: 'База знаний', to: '/administration/knowledge-base', icon: 'heroicons:book-open' },
    ],
  },
]

// Фильтрация меню по роли: ограниченные роли (manager) видят только свои разделы.
// Источник правды — shared/config/adminAccess (тот же, что в route-middleware).
const adminSession = useAdminSessionManager()
const visibleNav = computed<NavSection[]>(() => {
  const role = adminSession.getAdminRole()
  return NAV
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canAccessAdminPath(role, item.match ?? item.to)),
    }))
    .filter((section) => section.items.length > 0)
})

function isActive(item: NavItem): boolean {
  const base = item.match ?? item.to
  return route.path === base || route.path.startsWith(`${base}/`)
}

function logout() {
  ;(auth as unknown as { adminlogout: () => void }).adminlogout()
}
</script>

<template>
  <aside
    data-testid="admin-sidebar"
    class="fixed lg:static inset-y-0 left-0 z-50 lg:z-auto flex flex-col w-64 shrink-0 bg-hui-content1 lg:rounded-hui-lg transition-[width,transform] duration-200"
    :class="[
      collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64',
      mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
    ]"
  >
    <!-- Логотип + тоггл сворачивания -->
    <div class="flex items-center gap-2 h-14 px-3 shrink-0 border-b border-hui-divider">
      <div class="flex items-center justify-center w-9 h-9 shrink-0 rounded-hui-md bg-hui-primary text-hui-primary-foreground font-bold">
        T
      </div>
      <span class="font-semibold text-hui-foreground truncate" :class="collapsed ? 'lg:hidden' : ''">TasteOk</span>
      <button
        type="button"
        class="ml-auto lg:hidden inline-flex items-center justify-center w-8 h-8 rounded-hui-sm text-hui-foreground/60 hover:bg-hui-default-100 transition-colors outline-none"
        aria-label="Закрыть меню"
        @click="emit('close')"
      >
        <Icon name="heroicons:x-mark" class="w-5 h-5" />
      </button>
    </div>

    <!-- Навигация -->
    <nav class="flex-1 overflow-y-auto px-2 py-3 space-y-4">
      <div v-for="section in visibleNav" :key="section.title" class="space-y-1">
        <p
          class="px-3 text-[0.65rem] font-semibold uppercase tracking-wider text-hui-foreground/40"
          :class="collapsed ? 'lg:hidden' : ''"
        >
          {{ section.title }}
        </p>
        <NuxtLink
          v-for="item in section.items"
          :key="item.to"
          :to="item.to"
          class="flex items-center gap-3 px-3 h-10 rounded-hui-md text-sm transition-colors outline-none"
          :class="[
            isActive(item)
              ? 'bg-hui-primary/10 text-hui-primary font-medium'
              : 'text-hui-foreground/70 hover:bg-hui-default-100 hover:text-hui-foreground',
            collapsed ? 'lg:justify-center lg:px-0' : '',
          ]"
          :title="collapsed ? item.label : undefined"
          @click="emit('close')"
        >
          <Icon :name="item.icon" class="w-5 h-5 shrink-0" />
          <span class="truncate" :class="collapsed ? 'lg:hidden' : ''">{{ item.label }}</span>
        </NuxtLink>
      </div>
    </nav>

    <!-- Выход -->
    <div class="shrink-0 p-2 border-t border-hui-divider">
      <button
        type="button"
        class="w-full flex items-center gap-3 px-3 h-10 rounded-hui-md text-sm text-hui-danger hover:bg-hui-danger/10 transition-colors outline-none"
        :class="collapsed ? 'lg:justify-center lg:px-0' : ''"
        :title="collapsed ? 'Выйти' : undefined"
        @click="logout"
      >
        <Icon name="heroicons:arrow-right-on-rectangle" class="w-5 h-5 shrink-0" />
        <span class="truncate" :class="collapsed ? 'lg:hidden' : ''">Выйти</span>
      </button>
    </div>
  </aside>
</template>
